/**
 * Steel / aluminium section library.
 *
 * Every built-in section is an ILLUSTRATIVE EXAMPLE (`source: 'EXAMPLE'`): dimensions are common nominal sizes,
 * area and mass per metre are COMPUTED from the nominal outline (sharp corners, no root radii) and are therefore
 * estimates. They are not certified catalogue values. Catalogue or user sections (`source: 'CATALOG' | 'USER'`)
 * may state `areaMm2` / `massKgPerM`; stated values always win over computed ones.
 */
import { z } from 'zod';

export const SECTION_SHAPES = [
  'SHS',
  'RHS',
  'CHS',
  'ANGLE',
  'CHANNEL',
  'LIPPED_C',
  'IBEAM',
  'RECT',
  'POLY',
] as const;
export type SectionShape = (typeof SECTION_SHAPES)[number];

export const SECTION_SHAPE_LABEL: Record<SectionShape, string> = {
  SHS: 'Hộp vuông',
  RHS: 'Hộp chữ nhật',
  CHS: 'Ống tròn',
  ANGLE: 'Thép góc (V/L)',
  CHANNEL: 'Thép chữ U/C',
  LIPPED_C: 'Xà gồ C (có mép)',
  IBEAM: 'Thép chữ I/H',
  RECT: 'Tiết diện đặc chữ nhật',
  POLY: 'Tiết diện tuỳ chỉnh (đa giác)',
};

export const SECTION_SOURCES = ['CATALOG', 'EXAMPLE', 'USER'] as const;
export type SectionSource = (typeof SECTION_SOURCES)[number];

const mm = (max = 2000) => z.number().positive().max(max);

export const steelSectionSchema = z
  .object({
    id: z
      .string()
      .trim()
      .min(1)
      .max(60)
      .regex(/^[A-Za-z0-9._×x-]+$/, 'Mã tiết diện chỉ gồm chữ, số, . _ - x'),
    designation: z.string().trim().min(1).max(80),
    shape: z.enum(SECTION_SHAPES),
    /** Nominal dimensions, mm: h = depth (local y), b = width (local x), t = wall, tw/tf = web/flange, d = CHS diameter */
    dims: z.object({
      h: mm(),
      b: mm(),
      t: mm(100).optional(),
      tw: mm(100).optional(),
      tf: mm(100).optional(),
      lip: mm(200).optional(),
    }),
    /** POLY only: outline in mm, centred on the bounding box */
    outline: z
      .array(z.tuple([z.number(), z.number()]))
      .min(3)
      .max(200)
      .optional(),
    material: z.string().trim().min(1).max(60),
    densityKgM3: z.number().positive().max(20_000),
    /** Stated values (catalogue / user); absent ⇒ computed from the nominal outline */
    areaMm2: z.number().positive().max(1e6).optional(),
    massKgPerM: z.number().positive().max(10_000).optional(),
    source: z.enum(SECTION_SOURCES),
    note: z.string().max(300).optional(),
    /** Optional mechanical properties (not used for any check in this application) */
    props: z
      .object({
        IxCm4: z.number().positive().optional(),
        IyCm4: z.number().positive().optional(),
        WxCm3: z.number().positive().optional(),
        WyCm3: z.number().positive().optional(),
        fyMpa: z.number().positive().optional(),
      })
      .optional(),
  })
  .superRefine((s, ctx) => {
    const need = (k: keyof typeof s.dims) => {
      if (s.dims[k] === undefined)
        ctx.addIssue({ code: 'custom', message: `Thiếu kích thước ${k}`, path: ['dims', k] });
    };
    if (['SHS', 'RHS', 'CHS', 'ANGLE', 'LIPPED_C'].includes(s.shape)) need('t');
    if (s.shape === 'CHANNEL' || s.shape === 'IBEAM') {
      need('tw');
      need('tf');
    }
    if (s.shape === 'LIPPED_C') need('lip');
    if (s.shape === 'POLY' && !s.outline)
      ctx.addIssue({ code: 'custom', message: 'Thiếu đường bao', path: ['outline'] });
    const t = s.dims.t ?? 0;
    if (t && 2 * t >= Math.min(s.dims.h, s.dims.b))
      ctx.addIssue({ code: 'custom', message: 'Chiều dày quá lớn so với kích thước', path: ['dims', 't'] });
  });
export type SteelSection = z.infer<typeof steelSectionSchema>;

const STEEL = 'Thép SS400 (ví dụ)';
const STEEL_GALV = 'Thép mạ kẽm Z275 (ví dụ)';
const ALU = 'Nhôm 6005-T5 (ví dụ)';
const EX = 'Ví dụ minh hoạ — kiểm tra theo catalog nhà cung cấp trước khi dùng.';

const hollow = (
  id: string,
  shape: 'SHS' | 'RHS',
  h: number,
  b: number,
  t: number,
  material = STEEL,
  density = 7850,
  designation = `${shape} ${h}×${b}×${t}`,
) =>
  ({
    id,
    designation,
    shape,
    dims: { h, b, t },
    material,
    densityKgM3: density,
    source: 'EXAMPLE',
    note: EX,
  }) satisfies SteelSection;

/** Built-in illustrative sections. Ids are stable — saved projects reference them. */
export const BUILTIN_SECTIONS: SteelSection[] = [
  hollow('SHS50x50x2', 'SHS', 50, 50, 2),
  hollow('SHS60x60x2.5', 'SHS', 60, 60, 2.5),
  hollow('SHS75x75x3', 'SHS', 75, 75, 3),
  hollow('SHS100x100x4', 'SHS', 100, 100, 4),
  hollow('SHS125x125x5', 'SHS', 125, 125, 5),
  hollow('SHS150x150x5', 'SHS', 150, 150, 5),
  hollow('RHS100x50x3', 'RHS', 100, 50, 3),
  hollow('RHS150x100x4', 'RHS', 150, 100, 4),
  hollow('RHS200x100x5', 'RHS', 200, 100, 5),
  {
    id: 'CHS60.3x3.2',
    designation: 'CHS Ø60,3×3,2',
    shape: 'CHS',
    dims: { h: 60.3, b: 60.3, t: 3.2 },
    material: STEEL,
    densityKgM3: 7850,
    source: 'EXAMPLE',
    note: EX,
  },
  {
    id: 'CHS114.3x4',
    designation: 'CHS Ø114,3×4',
    shape: 'CHS',
    dims: { h: 114.3, b: 114.3, t: 4 },
    material: STEEL,
    densityKgM3: 7850,
    source: 'EXAMPLE',
    note: EX,
  },
  ...(
    [
      [40, 4],
      [50, 5],
      [63, 6],
    ] as const
  ).map(
    ([a, t]) =>
      ({
        id: `L${a}x${a}x${t}`,
        designation: `L${a}×${a}×${t}`,
        shape: 'ANGLE',
        dims: { h: a, b: a, t },
        material: STEEL,
        densityKgM3: 7850,
        source: 'EXAMPLE',
        note: EX,
      }) satisfies SteelSection,
  ),
  ...(
    [
      [100, 50, 20, 2],
      [150, 65, 20, 2.5],
      [200, 70, 20, 2.5],
    ] as const
  ).map(
    ([h, b, lip, t]) =>
      ({
        id: `C${h}x${b}x${lip}x${t}`,
        designation: `C${h}×${b}×${lip}×${t}`,
        shape: 'LIPPED_C',
        dims: { h, b, lip, t },
        material: STEEL_GALV,
        densityKgM3: 7850,
        source: 'EXAMPLE',
        note: EX,
      }) satisfies SteelSection,
  ),
  {
    id: 'U100x50x5',
    designation: 'U100×50×5',
    shape: 'CHANNEL',
    dims: { h: 100, b: 50, tw: 5, tf: 7.5 },
    material: STEEL,
    densityKgM3: 7850,
    source: 'EXAMPLE',
    note: EX,
  },
  {
    id: 'IPE200',
    designation: 'IPE 200',
    shape: 'IBEAM',
    dims: { h: 200, b: 100, tw: 5.6, tf: 8.5 },
    material: STEEL,
    densityKgM3: 7850,
    source: 'EXAMPLE',
    note: `${EX} Khối lượng tính theo đường bao danh nghĩa (không có bán kính lượn) nên nhỏ hơn bảng tra.`,
  },
  {
    id: 'H150x150x7x10',
    designation: 'H150×150×7×10',
    shape: 'IBEAM',
    dims: { h: 150, b: 150, tw: 7, tf: 10 },
    material: STEEL,
    densityKgM3: 7850,
    source: 'EXAMPLE',
    note: `${EX} Khối lượng tính theo đường bao danh nghĩa (không có bán kính lượn) nên nhỏ hơn bảng tra.`,
  },
  hollow('RAIL-AL40x40', 'RHS', 40, 40, 2, ALU, 2700, 'Rail nhôm 40×40 (ví dụ)'),
  hollow('MINIRAIL-AL45x40', 'RHS', 45, 40, 2, ALU, 2700, 'MiniRail nhôm 45×40 (ví dụ)'),
  {
    id: 'LFOOT-AL40x40',
    designation: 'Chân L nhôm 40×40×4',
    shape: 'ANGLE',
    dims: { h: 40, b: 40, t: 4 },
    material: ALU,
    densityKgM3: 2700,
    source: 'EXAMPLE',
    note: EX,
  },
  {
    id: 'ROD-12',
    designation: 'Thanh giằng Ø12 (vẽ dạng tiết diện đặc 12×12)',
    shape: 'RECT',
    dims: { h: 12, b: 12 },
    material: STEEL,
    densityKgM3: 7850,
    source: 'EXAMPLE',
    note: EX,
  },
];

export type Pt2 = [number, number];
export interface SectionOutline {
  outer: Pt2[];
  holes: Pt2[][];
}

const ring = (r: number, n = 20): Pt2[] =>
  Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    return [r * Math.cos(a), r * Math.sin(a)] as Pt2;
  });
const centre = (pts: Pt2[], b: number, h: number): Pt2[] => pts.map(([x, y]) => [x - b / 2, y - h / 2]);

/**
 * Cross-section outline in mm, counter-clockwise, centred on the bounding box.
 * Local x = width b, local y = depth h (h is aligned with the member's "up" direction).
 */
export function sectionOutline(s: SteelSection): SectionOutline {
  const { h, b } = s.dims;
  const t = s.dims.t ?? 0;
  switch (s.shape) {
    case 'SHS':
    case 'RHS':
      return {
        outer: centre(
          [
            [0, 0],
            [b, 0],
            [b, h],
            [0, h],
          ],
          b,
          h,
        ),
        holes: [
          centre(
            [
              [t, t],
              [t, h - t],
              [b - t, h - t],
              [b - t, t],
            ],
            b,
            h,
          ),
        ],
      };
    case 'CHS':
      return { outer: ring(h / 2), holes: [ring(h / 2 - t).reverse()] };
    case 'ANGLE':
      return {
        outer: centre(
          [
            [0, 0],
            [b, 0],
            [b, t],
            [t, t],
            [t, h],
            [0, h],
          ],
          b,
          h,
        ),
        holes: [],
      };
    case 'CHANNEL': {
      const tw = s.dims.tw!;
      const tf = s.dims.tf!;
      return {
        outer: centre(
          [
            [0, 0],
            [b, 0],
            [b, tf],
            [tw, tf],
            [tw, h - tf],
            [b, h - tf],
            [b, h],
            [0, h],
          ],
          b,
          h,
        ),
        holes: [],
      };
    }
    case 'LIPPED_C': {
      const lip = s.dims.lip!;
      return {
        outer: centre(
          [
            [0, 0],
            [b, 0],
            [b, lip],
            [b - t, lip],
            [b - t, t],
            [t, t],
            [t, h - t],
            [b - t, h - t],
            [b - t, h - lip],
            [b, h - lip],
            [b, h],
            [0, h],
          ],
          b,
          h,
        ),
        holes: [],
      };
    }
    case 'IBEAM': {
      const tw = s.dims.tw!;
      const tf = s.dims.tf!;
      const l = (b - tw) / 2;
      const r = (b + tw) / 2;
      return {
        outer: centre(
          [
            [0, 0],
            [b, 0],
            [b, tf],
            [r, tf],
            [r, h - tf],
            [b, h - tf],
            [b, h],
            [0, h],
            [0, h - tf],
            [l, h - tf],
            [l, tf],
            [0, tf],
          ],
          b,
          h,
        ),
        holes: [],
      };
    }
    case 'POLY':
      return { outer: s.outline!.map(([x, y]) => [x, y] as Pt2), holes: [] };
    case 'RECT':
    default:
      return {
        outer: centre(
          [
            [0, 0],
            [b, 0],
            [b, h],
            [0, h],
          ],
          b,
          h,
        ),
        holes: [],
      };
  }
}

function shoelace(p: Pt2[]): number {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!;
    const c = p[(i + 1) % p.length]!;
    s += a[0] * c[1] - c[0] * a[1];
  }
  return Math.abs(s) / 2;
}

/** Cross-sectional area (mm²): stated value, else computed from the nominal outline. */
export function sectionArea(s: SteelSection): { value: number; stated: boolean } {
  if (s.areaMm2) return { value: s.areaMm2, stated: true };
  if (s.shape === 'CHS') {
    const R = s.dims.h / 2;
    const r = R - (s.dims.t ?? 0);
    return { value: Math.PI * (R * R - r * r), stated: false };
  }
  const o = sectionOutline(s);
  return { value: shoelace(o.outer) - o.holes.reduce((t, h) => t + shoelace(h), 0), stated: false };
}

export type MassSource = 'STATED' | 'ESTIMATED_NOMINAL';

/** Mass per metre (kg/m) with its provenance. Estimated = nominal area × density (no root radii / coatings). */
export function sectionMass(s: SteelSection): { kgPerM: number; source: MassSource } {
  if (s.massKgPerM) return { kgPerM: s.massKgPerM, source: 'STATED' };
  const a = sectionArea(s);
  return { kgPerM: a.value * 1e-6 * s.densityKgM3, source: a.stated ? 'STATED' : 'ESTIMATED_NOMINAL' };
}

/** Section lookup: project sections first, then built-ins. */
export function sectionLibrary(custom: readonly SteelSection[] = []): Map<string, SteelSection> {
  const m = new Map<string, SteelSection>();
  for (const s of BUILTIN_SECTIONS) m.set(s.id, s);
  for (const s of custom) m.set(s.id, s);
  return m;
}

/** Visual envelope (m): half of the larger of depth / width — used for contact tests. */
export function sectionHalfSizeM(s: SteelSection | undefined): number {
  if (!s) return 0.025;
  return Math.max(s.dims.h, s.dims.b) / 2000;
}
